<template>
  <mf-card-container title="Faturamento" class="faturamento-card">
    <template #headers>
      <export-data class="mx-2" :data-fetcher="getDataToExport" filename="faturamento_mensal" ghost />
    </template>
    <mf-loading :boolean-loader="loading" loader-type="table">
      <v-container v-if="faturamentoTotalDoAno && faturamentoTotalDoAno.length" fluid class="pa-0" grid-list-xs>
        <v-layout>
          <v-flex v-for="(faturamento, faturamentoIndex) in faturamentoTotalDoAno" :key="faturamentoIndex" xs4>
            <p class="mb-0 subheading ml-4 year-title">{{ faturamento.find((e) => e.dataMoment.isValid).dataMoment.toFormat('yyyy') }}</p>
            <v-data-table :items="faturamento" :headers="headers" hide-default-footer disable-pagination>
              <template v-slot:item="props">
                <tr :class="{ 'grey-background': props.index === lineIndex }" @mouseover="setLineIndex(props.index)" @mouseleave="removeLineIndex()">
                  <td v-if="faturamento.length !== props.index + 1">
                    <span v-if="props.item.data" class="link" @click="goToMonthDailyRevenue(props.item.anoMes)">{{
                      DateTime.fromISO(props.item.anoMes).setLocale('pt-BR').toFormat('MMMM') | capitalize
                    }}</span>
                    <span v-if="!props.item.data">-</span>
                  </td>
                  <td v-else class="bold">{{ props.item.data }}</td>

                  <td v-if="props.item.valor">{{ props.item.valor | localeString(2) | currency }}</td>
                  <td v-else>-</td>

                  <td v-if="props.item.percentual">
                    <mf-tooltip v-if="lastIndex(faturamentoIndex, props.index, faturamento)" bottom variant="secondary">
                      <template v-slot:activator="{ on }">
                        <span style="display: flex; align-items: center; gap: 4px" v-on="on">
                          <mf-stonks variant="percentage" :percentage="props.item.percentual" />
                          <mf-icon small color="cinza-0">help</mf-icon>
                        </span>
                      </template>
                      <span>
                        Comparativo com o mesmo período do mês no ano anterior:
                        <span class="texto-corrido-destaque">
                          {{ DateTime.fromFormat(lastUpdate.last_updated.toString(), 'yyyyMMdd').minus({ years: 1 }).startOf('month').toFormat('dd/MM/yyyy') }}
                          a {{ DateTime.fromFormat(lastUpdate.last_updated.toString(), 'yyyyMMdd').minus({ years: 1 }).toFormat('dd/MM/yyyy') }}.</span
                        ><br />
                        Faturamento do mês no ano anterior até esta data:
                        <span class="texto-corrido-destaque">{{ faturamentoMesAnoAnterior.valor | localeString(2) | currency }}</span>
                      </span>
                    </mf-tooltip>
                    <mf-tooltip v-else-if="isLastYear(faturamentoIndex, props.index)" bottom variant="secondary">
                      <template v-slot:activator="{ on }">
                        <span style="display: flex; align-items: center; gap: 4px" v-on="on">
                          <mf-stonks variant="percentage" :percentage="props.item.percentual" />
                          <mf-icon small color="cinza-0">help</mf-icon>
                        </span>
                      </template>
                      <span>
                        Comparativo com o mesmo período do ano anterior:
                        <span class="texto-corrido-destaque">
                          {{ DateTime.fromFormat(lastUpdate.last_updated.toString(), 'yyyyMMdd').minus({ years: 1 }).startOf('year').toFormat('dd/MM/yyyy') }}
                          a {{ DateTime.fromFormat(lastUpdate.last_updated.toString(), 'yyyyMMdd').minus({ years: 1 }).toFormat('dd/MM/yyyy') }}.</span
                        ><br />
                        Faturamento do ano anterior até esta data:
                        <span class="texto-corrido-destaque">{{ faturamentoAnoAnterior.valor | localeString(2) | currency }}</span>
                      </span>
                    </mf-tooltip>
                    <span v-else>
                      <mf-stonks variant="percentage" :percentage="props.item.percentual" />
                    </span>
                  </td>
                  <td v-else>-</td>
                </tr>
              </template>
            </v-data-table>
          </v-flex>
        </v-layout>
      </v-container>
      <v-container v-else class="my-6">
        <v-row align="center">
          <v-col align="center">
            <span class="subtitle">Nenhum resultado encontrado</span>
          </v-col>
        </v-row>
      </v-container>
    </mf-loading>
  </mf-card-container>
</template>

<script>
import MfStonks from '@/modules/maria/produtos-impulsionadores/views/components/Step1/MfStonks.vue'
import ExportData from '@/components/export-data/ExportData.vue'
import capitalize from 'lodash/capitalize'
import last from 'lodash/last'
import findLastIndex from 'lodash/findLastIndex'
import groupBy from 'lodash/groupBy'
import { mapState, mapActions, mapGetters } from 'vuex'
import { mapWaitingActions } from 'vue-wait'
import { agrupamentoMensal, roundFloat } from '@/config/helpers.js'
import { DateTime } from 'luxon'

export default {
  components: {
    MfStonks,
    ExportData,
  },
  filters: {
    capitalize(value) {
      if (value) return capitalize(value)
      return ''
    },
  },
  props: {
    lista: [Array, Object],
    loading: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    faturamentoMesAnoAnterior: {
      valor: null,
      percentual: null,
    },
    faturamentoAnoAnterior: {
      valor: null,
      percentual: null,
    },
    lineIndex: null,
    DateTime: DateTime,
  }),
  computed: {
    ...mapState({
      filters: (state) => state.filters,
      lastUpdate: (state) => state.lastUpdate,
    }),
    ...mapGetters(['baseRevenueFilter']),
    headers() {
      return [
        {
          text: 'Mês',
          sortable: false,
          width: '30%',
        },
        {
          text: 'Faturamento',
          sortable: false,
          width: '50%',
        },
        {
          text: '%',
          sortable: false,
          width: '20%',
        },
      ]
    },
    formattedFaturamentoAnoAnterior() {
      let percentage = 0
      return {
        valor: this.faturamentoAnoAnterior.valor,
        percentual: percentage,
      }
    },
    faturamentoMensal() {
      return agrupamentoMensal(this.lista, 'VAL_FATURAMENTO')
    },
    faturamentoPreenchido() {
      const faturamentoPreenchido = this.faturamentoMensal.reduce((acc, fatMes) => {
        // First month
        if (!acc.length) return [fatMes]
        let dt = last(acc).dataMoment.plus({ months: 1 })
        let df = fatMes.dataMoment

        while (dt < df) {
          acc.push({
            ano: dt.year,
            mes: dt.month,
            dataMoment: dt,
            data: capitalize(dt.toFormat('MMMM/yy')),
            anoMes: dt.toFormat('yyyy-MM'),
            percentual: 0,
            valor: 0,
          })
          dt = dt.plus({ months: 1 })
        }
        acc.push(fatMes)
        return acc
      }, [])

      const firstMonth = faturamentoPreenchido[0]
      const lastMonth = last(faturamentoPreenchido)

      if (firstMonth) {
        for (let mes = firstMonth.mes - 1; mes >= 1; mes--) {
          faturamentoPreenchido.unshift({
            ano: firstMonth.dataMoment.minus({ months: firstMonth.mes - mes }).year,
            dataMoment: firstMonth.dataMoment.minus({ months: firstMonth.mes - mes }),
            data: null,
            percentual: 0,
            valor: 0,
          })
        }
      }

      if (lastMonth) {
        for (let mes = lastMonth.mes + 1; mes <= 12; mes++) {
          faturamentoPreenchido.push({
            ano: lastMonth.dataMoment.toFormat('yyyy'),
            dataMoment: lastMonth.dataMoment.plus({ months: mes - lastMonth.mes }),
            data: null,
            percentual: 0,
            valor: 0,
          })
        }
      }

      return faturamentoPreenchido
    },
    faturamentoMensalComPorcentagem() {
      const lastFaturamento = last(this.faturamentoPreenchido.filter((fat) => fat.valor))
      return this.faturamentoPreenchido.map((faturamento, index) => {
        const faturamentoAnoAnterior = this.faturamentoMensal.find((fatAnt) => fatAnt.mes === faturamento.mes && fatAnt.ano === faturamento.ano - 1)

        if (faturamentoAnoAnterior) {
          faturamento.percentual = (faturamento.valor / faturamentoAnoAnterior.valor - 1) * 100
        }

        if (faturamento.anoMes === lastFaturamento.anoMes) {
          faturamento.percentual = this.faturamentoMesAnoAnterior.percentual
        }
        return faturamento
      })
    },
    faturamentoMensalPorAno() {
      return Object.values(groupBy(this.faturamentoMensalComPorcentagem, 'ano'))
    },
    faturamentoTotalDoAno() {
      let lastYearRevenue = 0
      const faturamentosAnos = this.faturamentoMensalPorAno.map((ano, index, array) => {
        const revenuePerYear = []
        let faturamentoTotal = 0
        for (let mes of ano) {
          faturamentoTotal += mes.valor
          revenuePerYear.push(mes)
        }
        if (index !== 0) {
          let percentualTotal
          if (index === array.length - 1) {
            percentualTotal = this.faturamentoAnoAnterior.valor > 0 ? (faturamentoTotal / this.faturamentoAnoAnterior.valor - 1) * 100 : ''
          } else {
            percentualTotal = (faturamentoTotal / lastYearRevenue - 1) * 100
          }
          revenuePerYear.push({ ano: ano[0].ano, data: 'Total', valor: faturamentoTotal, percentual: percentualTotal })
        } else revenuePerYear.push({ ano: ano[0].ano, data: 'Total', valor: faturamentoTotal })
        lastYearRevenue = faturamentoTotal
        return revenuePerYear
      })
      return faturamentosAnos.slice(-3)
    },
  },
  async mounted() {
    await this.getFaturamentoMesAnoAnterior()
    await this.getFaturamentoAnoAnterior()
    this.$nextTick(() => {
      this.$bus.$on('filter', this.getFaturamentoMesAnoAnterior)
      this.$bus.$on('filter', this.getFaturamentoAnoAnterior)
    })
  },
  destroyed() {
    this.$bus.$off('filter')
  },
  methods: {
    ...mapWaitingActions({ empresaFaturamentoDta: 'faturamento-anterior', empresaFaturamentoRaw: 'faturamento-raw' }),
    ...mapActions(['setPreFilters']),
    getDataToExport() {
      const headers = ['Ano', 'Mês', 'Faturamento', 'Aumento em relação ao mesmo período do ano anterior']
      const rows = this.faturamentoTotalDoAno.reduce(
        (acc, yearRevenue) => [
          ...acc,
          ...yearRevenue
            .filter((monthlyRevenue) => monthlyRevenue.data !== 'Total')
            .map((monthlyRevenue) => [
              monthlyRevenue.ano,
              monthlyRevenue.dataMoment.month,
              monthlyRevenue.valor,
              roundFloat(monthlyRevenue.percentual / 100, 4),
            ]),
        ],
        []
      )
      return { headers, rows }
    },
    getFaturamentoMesAnoAnterior() {
      this.empresaFaturamentoDta({
        date: [DateTime.fromFormat(this.lastUpdate.last_updated.toString(), 'yyyyMMdd').toFormat('yyyy-MM')],
        unidades: this.filters.unitsConfiguration,
        categoria_cliente: this.filters.cliente,
        tipo_pessoa: this.filters.pessoa,
        fidelidade: this.filters.fidelidade,
        segmentos: this.filters.segments,
        produto: this.filters.products,
      }).then((data) => {
        this.faturamentoMesAnoAnterior.valor = data['VAL_FATURAMENTO_DTA']
        this.faturamentoMesAnoAnterior.percentual = data['PER_FATURAMENTO_DTA']
      })
    },
    getFaturamentoAnoAnterior() {
      const lastUpdateDate = DateTime.fromFormat(this.lastUpdate.last_updated.toString(), 'yyyyMMdd')
      this.empresaFaturamentoRaw({
        date: [lastUpdateDate.minus({ years: 1 }).startOf('year').toFormat('yyyy-MM-dd'), lastUpdateDate.minus({ years: 1 }).toFormat('yyyy-MM-dd')],
        unidades: this.filters.unitsConfiguration,
        categoria_cliente: this.filters.cliente,
        tipo_pessoa: this.filters.pessoa,
        fidelidade: this.filters.fidelidade,
        segmentos: this.filters.segments,
        produto: this.filters.products,
      }).then((data) => {
        this.faturamentoAnoAnterior.valor = data['VAL_FATURAMENTO']
      })
    },
    setLineIndex(index) {
      this.lineIndex = index
    },
    removeLineIndex() {
      this.lineIndex = null
    },
    lastIndex(arrayIndex, propertyIndex, faturamento) {
      const index = findLastIndex(faturamento.slice(0, -1), (object) => object.valor)
      const lastArray = Boolean(arrayIndex === this.faturamentoTotalDoAno.length - 1)
      const lastObject = Boolean(propertyIndex === index)
      return Boolean(lastArray && lastObject)
    },
    isLastYear(yearIndex, monthIndex) {
      return yearIndex === this.faturamentoTotalDoAno.length - 1 && monthIndex === this.faturamentoTotalDoAno[yearIndex].length - 1
    },
    goToMonthDailyRevenue(param) {
      this.setPreFilters(this.filters)
      let date = DateTime.fromFormat(param, 'yyyy-MM').toFormat('yyyy-MM')
      const newTab = this.$router.resolve({ path: `/empresa/faturamento/diario/${date}`, query: { is_prefilter: true } })
      window.open(newTab.href, '_blank')
    },
  },
}
</script>
<style lang="scss">
.faturamento-card {
  min-height: 250px;
  .bold {
    font-weight: bold;
  }
  .grey-background {
    background-color: $cinza-l90;
    transition: background-color 0.2s cubic-bezier(0.25, 0.8, 0.5, 1);
  }
  .year-title {
    color: rgba(0, 0, 0, 0.54);
  }
}
</style>
